import React, { useEffect, useState } from 'react';
import { AppState } from '$/store';
import { connect } from 'react-redux';
import HeadingBlock from '$/components/global/heading';
import { Link } from 'react-router-dom';
import Loading from '$/components/loading/Loading';
import * as Bixby from '$/core';
import { AgGridReact } from 'ag-grid-react';
import { GridApi, ColumnApi } from 'ag-grid-enterprise';
import NoDataFound from '$/components/global/noData';
import CreateClo from './add-clo/index';
import { getAllClos } from '$/store/clo/actions';
import dayjs from 'dayjs';
import { getIsCurrentUserSuperAdminState } from '$/store/profile/selectors';

interface ICloListProps {
  isLoading: boolean;
  error?: Bixby.Models.BixbyError;
  clos: Bixby.Models.CloSlim[] | undefined;
  dispatchGetClos: () => Promise<void>;
  isSuperAdmin: boolean;
}

const CloList: React.FC<ICloListProps> = props => {
  const { clos, isLoading, dispatchGetClos, isSuperAdmin } = props;
  const [isCreatingClo, setIsCreatingClo] = useState(false);
  const [, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);

  useEffect(() => {
    const getCloDispatcher = async () => {
      await dispatchGetClos();
      setIsCreatingClo(false);
    };
    getCloDispatcher();
  }, [dispatchGetClos]);

  const handleCloseModal = () => {
    setIsCreatingClo(false);
  };

  const handleOpenAddModal = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    setIsCreatingClo(true);
  };

  const cloCellRenderer = (props: any) => (
    <Link className="link_underline" to={`/admin/clo/${props.data.id}`}>
      {props.data.name}
    </Link>
  );

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  if (isLoading) {
    return <Loading pageLevel />;
  } else {
    return (
      <section className="clo_list">
        <HeadingBlock
          title={'Manage CLOs'}
          addButton={{ enabled: isSuperAdmin, text: 'Add New CLO', onClick: handleOpenAddModal }}
        />
        {clos && !Bixby.Utils.isNullOrEmpty(clos) ? (
          <div className="clo_list_datagrid">
            <div className="grid_container">
              <div className="grid ag-theme-alpine">
                <AgGridReact
                  defaultColDef={{
                    flex: 1,
                    minWidth: 100,
                    sortable: true,
                    resizable: true,
                    lockPinned: true,
                    filter: 'number',
                    cellClass: 'align-center',
                  }}
                  onGridReady={onGridReady}
                  rowData={clos}
                  suppressDragLeaveHidesColumns={true}
                  suppressMovableColumns={true}
                  animateRows={true}
                  rowHeight={35}
                  headerHeight={35}
                  components={{
                    cloCellRenderer,
                  }}
                  columnDefs={[
                    {
                      field: 'name',
                      headerName: 'CLO Name',
                      width: 200,
                      filter: 'agTextColumnFilter',
                      cellClass: 'align-left',
                      cellRenderer: 'cloCellRenderer',
                      floatingFilter: true,
                    },
                    {
                      field: 'bixbyCloId',
                      headerName: 'Bixby CLO Id',
                      width: 150,
                      filter: 'agTextColumnFilter',
                      cellClass: 'align-left',
                      floatingFilter: true,
                    },
                    {
                      field: 'manager',
                      headerName: 'Manager',
                      width: 150,
                      filter: 'agTextColumnFilter',
                      cellClass: 'align-left',
                      floatingFilter: true,
                    },
                    {
                      field: 'closingDate',
                      headerName: 'Closing Date',
                      width: 150,
                      filter: 'text',
                      cellClass: 'align-left',
                      valueFormatter: params => dayjs(params.value).format('MM/DD/YYYY'),
                    },
                  ]}></AgGridReact>
              </div>
            </div>
          </div>
        ) : (
          <NoDataFound dataType="clos" />
        )}
        {isSuperAdmin && <CreateClo onCloseModal={handleCloseModal} isCreatingClo={isCreatingClo} />}
      </section>
    );
  }
};

const mapStateToProps = (state: AppState) => {
  const { isSuperAdmin } = getIsCurrentUserSuperAdminState(state);
  return {
    isSuperAdmin,
    isLoading: state.cloState.isLoading,
    error: state.cloState.error,
    clos: state.cloState.clos,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchGetClos: () => dispatch(getAllClos()),
});

export default connect(mapStateToProps, mapDispatchToProps)(CloList);
